import React, { useState, useRef, useEffect } from 'react';
import { Box, FormControl, TextField, InputAdornment, Typography, Button, Paper, Popper, ClickAwayListener, } from '@mui/material';
import { DatePicker, StaticDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import 'dayjs/locale/pt-br';
import ErrorBox from '../boxes/ErrorBox';
import dayjs from 'dayjs';
import { getDateRangeByShortcut, isSameDateRange, parseFlexibleDate } from '../../../javascript/helpers/filtersHelpers';

const DateFilter = (props) => {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const anchorRef = useRef(null);
  const { clearFilter, sx, width, placeholder, label, onChange, value } = props;

  useEffect(() => {
    if (!value) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    const [startStr, endStr] = value.split(' - ');

    if (startStr && endStr) {
      setStartDate(parseFlexibleDate(startStr));
      setEndDate(parseFlexibleDate(endStr));
    }
  }, [value]);

  const handleOpen = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setOpen(true);
  };

  const applyShortcut = (type) => {
    const range = getDateRangeByShortcut(type);

    if (!range) return;

    setTempStartDate(range.start);
    setTempEndDate(range.end);
  };

  const handleCancel = () => {
    if (invalidDate) {
      setStartDate(null)
      setEndDate(null)
    }

    setTempStartDate(null);
    setTempEndDate(null);
    setOpen(false);
  };

  const handleConfirm = () => {
    if (!tempStartDate || !tempEndDate) return;

    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setOpen(false);

    const formattedRangeTime = `${tempStartDate.format('YYYY/MM/DD')} - ${tempEndDate.format('YYYY/MM/DD')}`;
    onChange?.(formattedRangeTime);
  };

  const invalidDate = tempStartDate && tempEndDate && (tempEndDate - tempStartDate) < 0;

  useEffect(() => {
    if (clearFilter) {
      setStartDate(null);
      setEndDate(null);
      setTempStartDate(null);
      setTempEndDate(null);
      setOpen(false);
    }
  }, [clearFilter]);

  const displayValue =
    startDate && endDate ? `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}` : '';

  const isShortcutSelected = (type) => isSameDateRange(tempStartDate, tempEndDate, type);

  const shortcutButtonProps = (type) => {
    const selected = isShortcutSelected(type);

    return {
      variant: selected ? 'contained' : 'outlined',
      color: selected ? 'success' : 'primary',
    };
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <Box>
        <FormControl sx={{ minWidth: width ?? '16rem', ...sx, }}>
          <TextField
            readOnly
            value={displayValue}
            onClick={handleOpen}
            placeholder={props.placeholder}
            label={props.label}
            inputRef={anchorRef}
            sx={{ cursor: 'pointer' }}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <ArrowDropDownRoundedIcon
                      sx={{
                        transition: 'transform 0.3s ease',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </InputAdornment>
                ),
              },
              inputLabel: { shrink: true },
            }}
          />
        </FormControl>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: 1300 }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                p: 8
              }
            },
          ]}
        >
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Paper sx={{ p: 2, display: 'flex', maxHeight: '90vh', overflowY: 'auto' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: '14rem', px: 2 }}>
                <Button {...shortcutButtonProps('today')} onClick={() => applyShortcut('today')}> Hoje </Button>
                <Button {...shortcutButtonProps('7days')} onClick={() => applyShortcut('7days')}> Últimos 7 dias </Button>
                <Button {...shortcutButtonProps('30days')} onClick={() => applyShortcut('30days')}> Últimos 30 dias </Button>
                <Button {...shortcutButtonProps('90days')} onClick={() => applyShortcut('90days')}> Últimos 90 dias </Button>
                <Button {...shortcutButtonProps('6months')} onClick={() => applyShortcut('6months')}> Últimos 6 meses </Button>
                <Button {...shortcutButtonProps('thisMonth')} onClick={() => applyShortcut('thisMonth')}> Este mês </Button>
              </Box>
              <Box display="flex" flexDirection="column">
                {invalidDate && (
                  <Box sx={{ my: 2 }}>
                    <ErrorBox errorMessage='Não é permitido data final anterior a data inicial' onClose={handleCancel} />
                  </Box>
                )}
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <DatePicker
                    label="Data inicial"
                    value={tempStartDate}
                    onChange={(newDate) => setTempStartDate(newDate)}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                  <Box>–</Box>
                  <DatePicker
                    label="Data final"
                    value={tempEndDate}
                    onChange={(newDate) => setTempEndDate(newDate)}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                </Box>
                <Box display="flex" gap={0} justifyContent="center" flexWrap="wrap" sx={{ py: 2 }}>
                  <StaticDatePicker
                    sx={{ flex: 1, minWidth: '200px', border: '1px solid #0000001F' }}
                    displayStaticWrapperAs="desktop"
                    value={tempStartDate}
                    onChange={(newValue) => setTempStartDate(newValue)}
                    slotProps={{
                      actionBar: { actions: undefined },
                    }}
                  />
                  <StaticDatePicker
                    sx={{ flex: 1, minWidth: '200px', border: '1px solid #0000001F' }}
                    displayStaticWrapperAs="desktop"
                    value={tempEndDate}
                    onChange={(newValue) => setTempEndDate(newValue)}
                    slotProps={{
                      actionBar: { actions: undefined },
                    }}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" pt={1}>
                  <Typography variant="body2" color="text.secondary">
                    {displayValue || ''}
                  </Typography>
                  <Box>
                    <Button onClick={handleCancel} variant="transparent">
                      <Typography variant="subtitle2" color="text.primary">
                        Cancelar
                      </Typography>
                    </Button>
                    <Button onClick={handleConfirm} variant="transparent" disabled={invalidDate}>
                      <Typography variant="subtitle2" color={invalidDate ? "text.disabled" : "primary"}>
                        Continuar
                      </Typography>
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Box>
    </LocalizationProvider>
  );
};

export default DateFilter;
